import jwtDecode from "jwt-decode";
import axios from "src/utils/axios";
import { createSlice } from "@reduxjs/toolkit";
import Roles from "src/utils/Roles";
import IsRole from "src/utils/IsRole";
import { GetAllEmployees } from "./employee";
import { getArqueos } from "./caja";
import { getDisplays } from "./displays";
import { loadTpvBootstrap } from "./bootstrap";
import { clearSession as clearEmployeeSessionState } from "./employeeSession";
import { restoreEmployeeSessionOnBoot } from "src/utils/employeeSessionRestore";
import { clearLocalStoragePreservingQr } from "src/utils/tmoQrStorage";
import { rememberExpiredNotice } from "src/utils/restaurantSession";
import { applySessionIdentifier } from "src/utils/deviceSessionCore";
import {
  ensureDeviceSessionIdentifier,
  resetDeviceSessionRecovery,
  verifyDeviceSession,
} from "src/utils/deviceSession";

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  isAuthenticated: false,
  user: {},
  status: 0,
  message: "",
  claims: [],
  isSessionAuthenticated: false,
  session: null,
  isRequestingAccess: false,
  closesSuccess: false,
  imFirstOnSession: false,
  userSessions: [],
  haveMoreSessionsThanAllowed: false,
};

const slice = createSlice({
  name: "authJwt",
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    stopLoading(state) {
      state.isLoading = false;
    },

    // INITIALISE
    getInitialize(state, action) {
      state.isLoading = false;
      state.isAuthenticated = action.payload.isAuthenticated;
      state.user = action.payload.user;
    },

    getInitializeSession(state, action) {
      state.isSessionAuthenticated = action.payload.isAuthenticated;
      state.session = action.payload.session;
      state.isRequestingAccess = false;
      state.isLoading = false;
    },

    // LOGIN
    loginSuccess(state, action) {
      state.isAuthenticated = true;
      state.user = action.payload.user;
    },

    // REGISTER
    registerSuccess(state, action) {
      state.isAuthenticated = true;
      state.user = action.payload.user;
    },

    // LOGOUT
    logoutSuccess(state) {
      state.isAuthenticated = false;
      state.user = null;
    },
    setUserSuccess(state, action) {
      state.user = action.payload;
    },

    // Patch parcial del user con los límites recalculados tras cambio de
    // add-ons. Lo usa el slice addOns.setAddOnQuantity al recibir la
    // respuesta del backend (AllowedMesas/Sessions ya recalculados allí),
    // para no depender de refreshTokenData() que tiene delay/cache.
    updateUserLimits(state, action) {
      if (!state.user) return;
      const {
        allowedMesas,
        allowedSessions,
        addOnId,
        quantity,
        pendingQuantity,
        pendingChangeAt,
      } = action.payload || {};
      if (typeof allowedMesas === "number") state.user.allowedMesas = allowedMesas;
      if (typeof allowedSessions === "number") state.user.allowedSessions = allowedSessions;
      if (addOnId != null) {
        const list = Array.isArray(state.user.userAddOns) ? [...state.user.userAddOns] : [];
        const idx = list.findIndex((u) => u.idAddOn === addOnId);
        // Si quantity baja a 0 efectivo (incremento confirmado) el isActive
        // pasa a false. Si hay pendingQuantity, el isActive sigue dependiendo
        // de quantity (sigue activo hasta fin de periodo).
        const isActiveFlag = quantity > 0;
        const next = {
          idAddOn: addOnId,
          quantity,
          isActive: isActiveFlag,
          pendingQuantity: pendingQuantity ?? null,
          pendingChangeAt: pendingChangeAt ?? null,
        };
        if (idx >= 0) {
          list[idx] = { ...list[idx], ...next };
        } else if (isActiveFlag) {
          list.push(next);
        }
        state.user.userAddOns = list;
      }
    },

    setClaimsSuccess(state, action) {
      state.claims = action.payload;
    },
    imFirstOnRequestingSession(state, action) {
      state.imFirstOnSession = action.payload.imFirst;
      state.session = action.payload.session;
    },
    startSessionSuccess(state, action) {
      state.isLoading = false;
      state.session = action.payload;
      state.startedSuccess = true;
      state.isSessionAuthenticated = true;
      state.isRequestingAccess = false;
    },
    startSessionOnlyQrSuccess(state, action) {
      state.isLoading = false;
      state.startedSuccess = true;
      state.session = action.payload;
      state.isSessionAuthenticated = true;
      state.isRequestingAccess = false;
    },
    receiveDataSuccess(state, action) {
      state.session = action.payload;
    },
    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = true;
      state.message = action.payload;
    },

    setHaveMoreSessions(state, action) {
      state.haveMoreSessionsThanAllowed = action.payload;
    },

    getCurrentSessions(state, action) {
      state.userSessions = action.payload;
    },

    deleteUserSession(state, action) {
      state.userSessions = state.userSessions.filter(
        (s) => s.id !== action.payload,
      );
    },

    requestPaymentSuccess(state, action) {
      state.isLoading = false;
      state.paymentSuccess = true;
      state.closesSuccess = true;
      state.session = action.payload;
      state.isRequestingAccess = false;
    },
  },
});

// Reducer
export default slice.reducer;

export const {
  imFirstOnRequestingSession,
  startSessionSuccess,
  startSessionOnlyQrSuccess,
  setUserSuccess,
  hasError,
  stopLoading,
  deleteUserSession,
  getCurrentSessions,
  setHaveMoreSessions,
  requestPaymentSuccess,
  updateUserLimits,
} = slice.actions;
// ----------------------------------------------------------------------

const isValidToken = (accessToken) => {
  if (!accessToken) {
    return false;
  }

  const decoded = jwtDecode(accessToken);
  const currentTime = Date.now() / 1000;

  return decoded.exp > currentTime;
};

const setSession = (
  accessToken,
  userId,
  rol,
  sessionId,
  userSessionIdentifier,
) => {
  if (accessToken) {
    localStorage.setItem("sessionId", sessionId);
    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("userId", userId);
    localStorage.setItem("rol", rol);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    // 2026-10-26 · Sin identificador ("" del registro por OTP, null en los
    // flujos QR) se borra en lugar de guardar ""/"null" y mandar la cabecera
    // vacía (el lease respondía 400 DEVICE_REQUIRED). Lo recupera
    // `recoverDeviceSession` en los TPV.
    applySessionIdentifier(userSessionIdentifier, { headers: axios.defaults.headers.common });

    // Re-aplicar SIEMPRE el UserEmployeeId persistido (no solo cuando está
    // vacío). Si la app se cierra con un empleado identificado y se vuelve
    // a abrir, queremos que el header axios refleje ese empleado desde el
    // primer request que dispare el DashboardLayout — sin esto, dependíamos
    // del header inicial que `utils/axios.js` setea al cargar el módulo,
    // y un orden de import alterado dejaba el header en 0 mientras Redux
    // mostraba al empleado identificado (peticiones trazadas como admin
    // pero UI mostrando al empleado correcto, inconsistencia silenciosa).
    const persistedEmployeeId = localStorage.getItem("UserEmployeeId");
    if (persistedEmployeeId === null) {
      localStorage.setItem("UserEmployeeId", 0);
      axios.defaults.headers.common.UserEmployeeId = 0;
    } else {
      axios.defaults.headers.common.UserEmployeeId = Number(persistedEmployeeId);
    }
  } else {
    localStorage.removeItem("sessionId");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("userId");
    localStorage.removeItem("rol");
    localStorage.removeItem("sessionIdentifier");
    localStorage.removeItem("UserEmployeeId");
    localStorage.removeItem("SessionKind");
    localStorage.removeItem("EmployeeSessionToken");
    localStorage.removeItem("EmployeeSessionInfo");
    localStorage.removeItem("EmployeeSessionExpiresAt");

    delete axios.defaults.headers.common.Authorization;
    delete axios.defaults.headers.common.SessionIdentifier;
    delete axios.defaults.headers.common.UserEmployeeId;
    resetDeviceSessionRecovery();
  }
};

const closeDataSession = () => {
  localStorage.removeItem("sessionId");
  localStorage.removeItem("accessToken");
  localStorage.removeItem("comensalId");
  localStorage.removeItem("UserEmployeeId");

  var user = window.localStorage.getItem("userId");

  if (user === null || user === "undefined") {
    // 2026-10-26 · El identificador del DISPOSITIVO solo se suelta sin
    // restaurante logueado. Antes se borraba siempre: con el TPV abierto en
    // el mismo navegador que una sesión QR, la siguiente recarga arrancaba
    // con la cabecera SessionIdentifier vacía (lease → 400 DEVICE_REQUIRED).
    localStorage.removeItem("sessionIdentifier");
    delete axios.defaults.headers.common.Authorization;
    delete axios.defaults.headers.common.SessionIdentifier;
    delete axios.defaults.headers.common.UserEmployeeId;
  }
};

// ----------------------------------------------------------------------

export function updateUser(user) {
  return async (dispatch) => {
    dispatch(slice.actions.setUserSuccess(user));
  };
}

// F2 · Persistir sesión firmada `X-Employee-Session` cuando el backend la
// emite junto con el login (empleado autenticado por email+password). El
// interceptor axios la lee de localStorage y la inyecta como header en
// todas las requests siguientes. Se usa desde `login` y `verifyOtpAndLogin`.
function persistEmployeeSessionIfPresent(data) {
  const token = data?.employeeSessionToken;
  const expires = data?.employeeSessionExpiresAt;
  const info = data?.employeeSession;
  if (!token) return;
  try {
    window.localStorage.setItem("EmployeeSessionToken", token);
    window.localStorage.setItem(
      "EmployeeSessionInfo",
      JSON.stringify(info || null),
    );
    window.localStorage.setItem("EmployeeSessionExpiresAt", expires || "");
  } catch { /* localStorage no disponible */ }
}

// Limpia CUALQUIER residuo de sesión de empleado. Se llama al hacer un
// login OWNER (email+password del dueño o QR admin genérico) — sin esto,
// el `UserEmployeeId` y el `EmployeeSessionToken` de la sesión anterior
// quedan en localStorage y el TPV cree que hay un empleado activo.
// Peligro real: el header `UserEmployeeId=X` viaja en cada request y el
// backend lo interpreta como sesión legacy → cae al bypass del owner y
// deja pasar acciones que tocaría restringir al empleado. Fix 2026-07-31.
// 2026-08-27 · Ahora recibe `dispatch` opcional para poder también
// limpiar el slice Redux `employeeSession` en el mismo paso. Sin esto,
// `state.employeeSession.info` seguía con el empleado anterior aunque
// localStorage estuviera limpio → `useCanDo` creía que había sesión de
// empleado activa y `RequireAction` bloqueaba al admin con
// NoPermissionView pese a ser el dueño. Los callers que ya tenían la
// función deben pasar el dispatch — se mantiene retrocompatible.
function clearEmployeeSessionResidue(dispatch) {
  try {
    window.localStorage.setItem("UserEmployeeId", 0);
    window.localStorage.removeItem("EmployeeSessionToken");
    window.localStorage.removeItem("EmployeeSessionInfo");
    window.localStorage.removeItem("EmployeeSessionExpiresAt");
    window.localStorage.removeItem("EmployeeSessionOwner");
  } catch { /* localStorage no disponible */ }
  axios.defaults.headers.common.UserEmployeeId = 0;
  if (typeof dispatch === "function") {
    dispatch(clearEmployeeSessionState());
  }
}

export function login({ email, password }) {
  return async (dispatch) => {
    // Limpiamos ANTES de la request del login para que el header
    // `UserEmployeeId` y `X-Employee-Session` residual de la sesión
    // anterior no filtren en los dispatchs subsiguientes (bootstrap,
    // getArqueos, GetAllEmployees…) antes de que la rama else del
    // "SessionKind" tenga tiempo de resetearlos. Si el login trae
    // employeeId, más abajo se reasigna el nuevo.
    clearEmployeeSessionResidue(dispatch);

    const res = await axios.post(`authentication/login`, {
      email: email,
      password: password,
    });

    const {
      token,
      user,
      userSessionIdentifier,
      haveMoreSessionsThanAllowed,
      employeeId,
    } = res.data;

    const claims = jwtDecode(token);
    setSession(token, user.id, claims.role, null, userSessionIdentifier);
    dispatch(recoverDeviceSession(claims.role, { fresh: true }));

    const sessionsIdentifiers = res.data.sessions || [];

    dispatch(
      getCurrentSessions(
        sessionsIdentifiers?.filter((s) => {
          return s.userSessionIdentifier != userSessionIdentifier;
        }),
      ),
    );

    dispatch(setHaveMoreSessions(haveMoreSessionsThanAllowed || false));

    dispatch(slice.actions.setClaimsSuccess(claims));

    dispatch(slice.actions.loginSuccess({ user }));

    if (employeeId) {
      window.localStorage.setItem("UserEmployeeId", employeeId);
      axios.defaults.headers.common.UserEmployeeId = employeeId;
      persistEmployeeSessionIfPresent(res.data);
      // Marca "esta sesión web se autenticó con credenciales de EMPLEADO"
      // (no de admin dueño). Se usa para bloquear cambio de usuario,
      // ocultar el wizard de onboarding y otras acciones que solo
      // corresponden al owner. NOTA: el JWT en este flujo se emite
      // con la identidad del restaurante padre, así que `claims.role`
      // sigue siendo UserNormal — no sirve para distinguir. Esta flag
      // sí lo hace de forma explícita.
      window.localStorage.setItem("SessionKind", "employee-web");
    } else {
      // Login del dueño: sesión "owner". Sobrescribimos por si venía
      // marcada como employee-web de una sesión anterior mal cerrada.
      // Además limpiamos CUALQUIER residuo del empleado anterior — sin
      // esto, `UserEmployeeId` y `EmployeeSessionToken` seguían activos
      // y el TPV operaba como si el empleado siguiera identificado
      // (con los riesgos de permisos que eso implica).
      window.localStorage.setItem("SessionKind", "owner");
      clearEmployeeSessionResidue(dispatch);
    }

    if (
      !IsRole(claims.role, Roles.GLOBAL_ADMIN) &&
      !IsRole(claims.role, Roles.USER_DISTRIBUTOR)
    ) {
      // Bootstrap unificado: 1 GET a /api/bootstrap/tpv en lugar de 7
      // GETs paralelos (getMesas, GetZonas, getProducts, getCategories,
      // getTags, getFrequentProducts + alérgenos). El backend resuelve
      // los bloques con Task.WhenAll, así que el TTFB es el max, no la
      // suma. Reduce round trips + preflight OPTIONS.
      dispatch(loadTpvBootstrap());
      // Los siguientes NO están en el bootstrap porque tienen semántica
      // distinta (paginación, hub sync, uso puntual):
      dispatch(getDisplays(false));
      dispatch(getArqueos());
      dispatch(GetAllEmployees(employeeId));
    }
  };
}

// ----------------------------------------------------------------------
// Bloque A onboarding v2 · Registro público con trial 30d sin CC.
// El registro real se dispara desde la LANDING (form inline); aquí
// solo verificamos el OTP y arrancamos la sesión. Reutiliza el mismo
// `setSession` + `loginSuccess` que `login()` para no divergir.

export function verifyOtpAndLogin({ email, code }) {
  return async (dispatch) => {
    clearEmployeeSessionResidue(dispatch);

    const res = await axios.post(`authentication/VerifyEmailOtp`, {
      email,
      code,
    });

    const {
      token,
      user,
      userSessionIdentifier,
      haveMoreSessionsThanAllowed,
      employeeId,
    } = res.data;

    const claims = jwtDecode(token);
    setSession(token, user.id, claims.role, null, userSessionIdentifier);
    dispatch(recoverDeviceSession(claims.role, { fresh: true }));

    const sessionsIdentifiers = res.data.sessions || [];
    dispatch(
      getCurrentSessions(
        sessionsIdentifiers?.filter(
          (s) => s.userSessionIdentifier !== userSessionIdentifier,
        ),
      ),
    );
    dispatch(setHaveMoreSessions(haveMoreSessionsThanAllowed || false));
    dispatch(slice.actions.setClaimsSuccess(claims));
    dispatch(slice.actions.loginSuccess({ user }));

    if (employeeId) {
      window.localStorage.setItem("UserEmployeeId", employeeId);
      axios.defaults.headers.common.UserEmployeeId = employeeId;
      persistEmployeeSessionIfPresent(res.data);
      window.localStorage.setItem("SessionKind", "employee-web");
    } else {
      window.localStorage.setItem("SessionKind", "owner");
      clearEmployeeSessionResidue(dispatch);
    }

    // Post-verify NO cargamos bootstrap TPV — el user va al wizard sin
    // datos aún (mesas, categorías, productos vacíos). El wizard hará
    // sus propios GETs específicos y cuando termine, `login()` normal.
    return res.data;
  };
}

/**
 * Solicita un nuevo OTP invalidando el anterior. Rate-limited server-side
 * (1/min por email → 429). El caller debe manejar el 429 para pintar UX.
 */
export function resendOtp(email) {
  return async () => {
    const res = await axios.post(`authentication/ResendOtp`, { email });
    return res.data; // { email, otpExpiresAt }
  };
}

// ----------------------------------------------------------------------
export function setToken(res) {
  return async (dispatch) => {
    clearEmployeeSessionResidue(dispatch);

    const {
      token,
      user,
      userSessionIdentifier,
      haveMoreSessionsThanAllowed,
      employeeId,
    } = res.data;

    const claims = jwtDecode(token);
    setSession(token, user.id, claims.role, null, userSessionIdentifier);
    dispatch(recoverDeviceSession(claims.role, { fresh: true }));

    const sessionsIdentifiers = res.data.sessions || [];

    dispatch(
      getCurrentSessions(
        sessionsIdentifiers?.filter((s) => {
          return s.userSessionIdentifier != userSessionIdentifier;
        }),
      ),
    );

    dispatch(setHaveMoreSessions(haveMoreSessionsThanAllowed || false));

    dispatch(slice.actions.setClaimsSuccess(claims));

    dispatch(slice.actions.loginSuccess({ user }));

    if (employeeId) {
      window.localStorage.setItem("UserEmployeeId", employeeId);
      axios.defaults.headers.common.UserEmployeeId = employeeId;
      persistEmployeeSessionIfPresent(res.data);
      // Login por QR PERSONAL de empleado: mismo marcado que login web.
      // El endpoint `/employee/login/{token}` es el que sirve al escanear
      // el QR generado desde la ficha del empleado. Devuelve `employeeId`
      // poblado — señal inequívoca de que es sesión personal.
      window.localStorage.setItem("SessionKind", "employee-web");
    } else {
      // QR "genérico" del admin (sin employeeId) → sesión owner.
      window.localStorage.setItem("SessionKind", "owner");
      clearEmployeeSessionResidue(dispatch);
    }

    if (
      !IsRole(claims.role, Roles.GLOBAL_ADMIN) &&
      !IsRole(claims.role, Roles.USER_DISTRIBUTOR)
    ) {
      // Mismo bootstrap unificado que en el flow de login normal.
      dispatch(loadTpvBootstrap());
      dispatch(getArqueos());
      dispatch(GetAllEmployees(employeeId));
    }
  };
}

// ----------------------------------------------------------------------

export function register({
  email,
  password,
  firstName,
  lastName,
  direccion,
  provincia,
  localidad,
  telefono,
  nif,
}) {
  return async (dispatch) => {
    const response = await axios.post(
      `authentication/register`,
      JSON.stringify({
        firstName: firstName,
        lastName: lastName,
        email: email,
        password: password,
        location: "Alicante",
        localidad: localidad,
        direccion: direccion,
        provincia: provincia,
        telefono: telefono,
        nif: nif,
        country: "Spain",
        birthDate: "2021-04-29T08:28:02.400Z",
      }),
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    const { token, user } = response.data;
    const claims = jwtDecode(token);
    setSession(token, user.id, claims.role, null);
    dispatch(slice.actions.registerSuccess({ user }));
  };
}

// ----------------------------------------------------------------------

export function logout() {
  return async (dispatch) => {
    // Cerrar todas las conexiones SignalR activas ANTES de limpiar la sesión.
    // Evita que queden websockets huérfanos intentando reconectar con el token
    // antiguo, y libera los listeners Redux antes de perder el store.
    try {
      const { closeAllConnections } = await import("src/utils/hubs/HubConnectionManager");
      closeAllConnections();
    } catch (err) {
      console.warn("Error cerrando conexiones SignalR al logout:", err);
    }

    setSession(null);
    // Limpiamos también el slice Redux del empleado — sin esto, si el
    // siguiente login del browser es del ADMIN, `useCanDo` seguía viendo
    // el `info` residual del empleado anterior y `RequireAction` mostraba
    // NoPermissionView pese a ser el dueño.
    clearEmployeeSessionResidue(dispatch);
    dispatch(slice.actions.logoutSuccess());
  };
}

// ----------------------------------------------------------------------

// 2026-10-28 · Renovación del JWT del restaurante: la MISMA llamada y el mismo
// guardado al arrancar (`getInitialize`) y en la renovación silenciosa con la
// app abierta (`renewRestaurantToken`, utils/restaurantSessionRuntime).
async function requestRenewedRestaurantToken(user, role, sessionIdentifier, config) {
  const res = await axios.get(`authentication/user/${user}`, config);
  if (res.status === 401) {
    const err = new Error("RESTAURANT_SESSION_REJECTED");
    err.status = 401;
    err.response = { status: 401, data: res.data };
    throw err;
  }
  setSession(res.data.token, user, role, null, sessionIdentifier);
  return res;
}

/**
 * Renueva el token del restaurante sin recargar (no toca la sesión PIN del
 * empleado). Rechaza con `status: 401` si el servidor no la renueva (sesión
 * caducada / revocada); cualquier otro rechazo es un fallo de red/servidor.
 */
export function renewRestaurantToken() {
  return async (dispatch) => {
    const accessToken = window.localStorage.getItem("accessToken");
    const user = window.localStorage.getItem("userId");
    const sessionIdentifier = window.localStorage.getItem("sessionIdentifier");
    const claims = jwtDecode(accessToken);
    // El 401 llega como respuesta (el rechazo del interceptor con el cuerpo
    // vacío del reto JWT no conserva el status) para distinguirlo de la red.
    const res = await requestRenewedRestaurantToken(user, claims.role, sessionIdentifier, {
      __isRestaurantRenewal: true,
      skipDedupe: true,
      validateStatus: (status) => (status >= 200 && status < 300) || status === 401,
    });
    if (res.data?.user) dispatch(slice.actions.setUserSuccess(res.data.user));
    return res.data.token;
  };
}

export function getInitialize() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const accessToken = window.localStorage.getItem("accessToken");
      const user = window.localStorage.getItem("userId");
      const sessionIdentifier =
        window.localStorage.getItem("sessionIdentifier");

      if (accessToken && isValidToken(accessToken) && user != null) {
        const claims = jwtDecode(accessToken);
        setSession(accessToken, user, claims.role, null, sessionIdentifier);

        // 2026-10-02 · Tras recargar se mantiene en silencio el empleado
        // identificado por PIN (antes se borraba su token y las peticiones
        // salían como el dueño mientras la UI mostraba al empleado). Solo
        // se limpia un resto real: el dueño opera o el token es de otro
        // login (guard 2026-08-27 contra NoPermissionView al admin). La
        // sesión viva se revalida con el servidor; si la rechaza, se
        // re-emite (sin PIN) o se pide el PIN.
        dispatch(restoreEmployeeSessionOnBoot({ clearResidue: clearEmployeeSessionResidue }));

        dispatch(slice.actions.setClaimsSuccess(claims));
        const res = await requestRenewedRestaurantToken(user, claims.role, sessionIdentifier);
        dispatch(recoverDeviceSession(claims.role, { verify: true }));

        const sessions = res.data.sessions || [];

        dispatch(
          getCurrentSessions(
            sessions.filter((s) => {
              return s.userSessionIdentifier != sessionIdentifier;
            }),
          ),
        );

        dispatch(
          setHaveMoreSessions(res.data.haveMoreSessionsThanAllowed || false),
        );

        if (
          !IsRole(claims.role, Roles.GLOBAL_ADMIN) &&
          !IsRole(claims.role, Roles.USER_DISTRIBUTOR)
        )
          dispatch(getArqueos());

        dispatch(
          slice.actions.getInitialize({
            isAuthenticated: true,
            user: res.data.user,
          }),
        );
      } else {
        // 2026-10-28 · Token guardado pero ya vencido: al login con el aviso.
        if (accessToken && user != null && user !== "null") rememberExpiredNotice();
        setSession(null);

        dispatch(
          slice.actions.getInitialize({
            isAuthenticated: false,
            user: null,
          }),
        );
      }
    } catch (error) {
      setSession(null);
      dispatch(
        slice.actions.getInitialize({
          isAuthenticated: false,
          user: null,
        }),
      );
    }
  };
}

export function refreshTokenData() {
  return async (dispatch) => {
    try {
      const user = window.localStorage.getItem("userId");
      const sessionIdentifier =
        window.localStorage.getItem("sessionIdentifier");
      const res = await axios.get(`authentication/user/${user}`);

      const claims = jwtDecode(res.data.token);

      setSession(res.data.token, user, claims.role, null, sessionIdentifier);
      dispatch(recoverDeviceSession(claims.role));

      dispatch(
        slice.actions.getInitialize({
          isAuthenticated: true,
          user: res.data.user,
        }),
      );

      dispatch(slice.actions.setClaimsSuccess(claims));
    } catch (error) {
      setSession(null);
      dispatch(
        slice.actions.getInitialize({
          isAuthenticated: false,
          user: null,
        }),
      );
    }
  };
}

// 2026-10-26 · Un TPV (restaurante o empleado web) siempre opera con su
// identificador de dispositivo. Si no lo tiene (registro por OTP antiguo,
// almacenamiento borrado…), se pide al API en lugar de dejar que el lease de
// mesa falle con 400 DEVICE_REQUIRED en cada entrada. Administrador y
// comercial no tienen dispositivo. `fresh`: login nuevo → se puede reintentar
// aunque otra sesión anterior de esta carga no lo consiguiera. `verify`: al
// arrancar se comprueba con el API que el dispositivo sigue activo.
export const isDeviceSessionRole = (role) =>
  !!role && !IsRole(role, Roles.GLOBAL_ADMIN) && !IsRole(role, Roles.USER_DISTRIBUTOR);

export function recoverDeviceSession(role, { fresh = false, verify = false } = {}) {
  return async (dispatch) => {
    if (!isDeviceSessionRole(role)) return null;
    if (fresh) resetDeviceSessionRecovery();
    const before = axios.defaults?.headers?.common?.SessionIdentifier || null;
    let sid = null;
    try {
      // `verify` (arranque con sesión guardada): el API confirma que el
      // dispositivo sigue activo; si se cerró desde otro dispositivo, se
      // vuelve al login con aviso (PrivateUserSessionHub escucha el evento).
      sid = await (verify ? verifyDeviceSession() : ensureDeviceSessionIdentifier());
    } catch {
      return null;
    }
    // Dispositivo dado de alta ahora: refrescar la lista de sesiones (y el
    // aviso de «más sesiones de las permitidas»).
    if (sid && sid !== before) {
      try {
        await dispatch(refreshSessions());
      } catch {
        /* informativo */
      }
    }
    return sid;
  };
}

export function refreshSessions() {
  return async (dispatch) => {
    // Try/catch defensivo: el hub SignalR sigue vivo unos ms tras el
    // logout y puede lanzar este fetch cuando el token ya es inválido
    // → 401 ruidoso en consola sin efecto útil. Al no haber sesión
    // activa, la respuesta no aporta nada al usuario que se está
    // yendo, así que tragamos el 401 en silencio.
    try {
      const res = await axios.get(`authentication/getOnlySessions`);

      const sessions = res.data.sessions || [];

      // Guardamos el listado COMPLETO (incluida la sesión actual). Los
      // consumidores que necesiten distinguirla comparan
      // `row.userSessionIdentifier === localStorage.sessionIdentifier`.
      // Sin la actual, ClockingDeviceCard y el gate del TopBar dejan
      // de funcionar. Mismo criterio que `getSessionData` (ver comment
      // allí).
      dispatch(getCurrentSessions(sessions));

      dispatch(
        setHaveMoreSessions(res.data.haveMoreSessionsThanAllowed || false),
      );
    } catch (err) {
      if (err?.response?.status === 401) return; // ignore silently
      throw err;
    }
  };
}

export function getInitializeSession(isOnlyQr, onlySync = false) {
  return async (dispatch) => {
    if (!onlySync) dispatch(slice.actions.startLoading());

    try {
      const sessionId = window.localStorage.getItem("sessionId");
      const tokenStorage = window.localStorage.getItem("accessToken");
      const comensal = window.localStorage.getItem("comensalId");

      if (tokenStorage && isValidToken(tokenStorage)) {
        const response = await axios.get(`authentication`, {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "Access-Control-Allow-Origin": "*",
            Authorization: `Bearer ${tokenStorage}`,
          },
        });
        const { token, user } = response.data;

        if (isOnlyQr) {
          setSession(token, user, null, null, null);
          dispatch(slice.actions.stopLoading());
          return;
        }

        setSession(token, null, null, sessionId, null);
        var getActual;
        if (sessionId) {
          getActual = await axios.get(`data/actual/session/${sessionId}`);
        } else if (comensal) {
          getActual = await axios.get(`data/actual/comensal/${comensal}`);
        }
        if (getActual.data.session === null) {
          //pendiente
          dispatch(
            slice.actions.getInitializeSession({
              isAuthenticated: false,
              session: null,
            }),
          );
        } else {
          const invalidStates =
            getActual.data.session.isClosed || !getActual.data.session.isActive;

          dispatch(
            slice.actions.getInitializeSession({
              isAuthenticated: !invalidStates,
              session: getActual.data.session,
            }),
          );
          setSession(token, null, null, getActual.data.session.id, null);
        }
      } else {
        dispatch(
          slice.actions.getInitializeSession({
            isAuthenticated: false,
            session: null,
          }),
        );
      }
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);

      // 2026-09-27 · Chequeo defensivo — cuando el server está caído
      // o hay error de red, `error` puede ser `undefined` o no tener
      // `.status` (axios usa `error.response?.status`, otros clientes
      // solo `.status`). Antes petaba con "Cannot read properties of
      // undefined (reading 'status')" y crasheaba la app. Ahora
      // resolvemos con optional chaining y default seguro.
      const status = error?.status ?? error?.response?.status;
      if (status === 401) {
        closeDataSession();
        if (!window.localStorage.getItem("userId")) clearLocalStoragePreservingQr();
      }
      dispatch(
        slice.actions.getInitializeSession({
          isAuthenticated: false,
          session: null,
        }),
      );
    }
  };
}

export function closeSessionFromHub() {
  return async (dispatch) => {
    closeDataSession();
    if (!window.localStorage.getItem("userId")) clearLocalStoragePreservingQr();

    dispatch(
      slice.actions.getInitializeSession({
        isSessionAuthenticated: false,
        session: null,
      }),
    );
  };
}

export function startSession(session) {
  return async (dispatch) => {
    try {
      const response = await axios.post(
        "data/startsession",
        JSON.stringify({
          idMesa: session.mesa,
          idRestaurante: session.restaurante,
        }),
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "Access-Control-Allow-Origin": "*",
          },
        },
      );

      setSession(
        response.data.session.token,
        null,
        null,
        response.data.session.id,
        null,
      );
      dispatch(slice.actions.startSessionSuccess(response.data.session));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error.message));
    }
  };
}

export function receiveDataFromHub(session) {
  return async (dispatch) => {
    if (session.isClosed) {
      closeDataSession();
      if (!window.localStorage.getItem("userId")) clearLocalStoragePreservingQr();

      dispatch(
        slice.actions.getInitializeSession({
          isSessionAuthenticated: false,
          session: null,
        }),
      );
    } else {
      dispatch(slice.actions.receiveDataSuccess(session));
    }
  };
}

// ----------------------------------------------------------------------

export function getSessionData() {
  return async (dispatch) => {
    await axios.get(`authentication/GetSessions`).then((res) => {
      // Guardamos la lista COMPLETA (incluida la sesión actual). Los
      // consumidores que necesiten distinguirla comparan
      // `row.userSessionIdentifier === localStorage.sessionIdentifier`.
      // Filtrarla aquí rompía flujos que sí necesitan la fila propia
      // (p.ej. ClockingDeviceCard: sin la actual en el array,
      // `currentSession` siempre era undefined y `currentIsClocking`
      // siempre false → el botón "Marcar ESTE" no desaparecía nunca
      // aunque el backend hubiera puesto el flag a true).
      dispatch(slice.actions.getCurrentSessions(res.data || []));
    });
  };
}

// ----------------------------------------------------------------------
export function requestPaymentSession(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`data/payment/${id}`);
      dispatch(slice.actions.requestPaymentSuccess(response.data.session));
      // closeDataSession();
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}
